/**
 * Curated hero imagery for the site.
 * Two sources: CDN-hosted asset.json pointers + bundled topic-specific heroes.
 */

import accountingAsset from "@/assets/heroes/hero-accounting.jpg.asset.json";
import chartAsset from "@/assets/heroes/hero-chart.jpg.asset.json";
import consultationAsset from "@/assets/heroes/hero-consultation.jpg.asset.json";
import irsBuildingAsset from "@/assets/heroes/hero-irs-building.jpg.asset.json";
import moneyAsset from "@/assets/heroes/hero-money.jpg.asset.json";
import noticeAsset from "@/assets/heroes/hero-notice.jpg.asset.json";
import savingsAsset from "@/assets/heroes/hero-savings.jpg.asset.json";
import signingAsset from "@/assets/heroes/hero-signing.jpg.asset.json";
import taxFormsAsset from "@/assets/heroes/hero-tax-forms.jpg.asset.json";

import wageGarnishmentUrl from "@/assets/heroes/hero-wage-garnishment.jpg";
import bankLevyUrl from "@/assets/heroes/hero-bank-levy.jpg";
import auditUrl from "@/assets/heroes/hero-audit.jpg";
import settlementUrl from "@/assets/heroes/hero-settlement.jpg";
import installmentUrl from "@/assets/heroes/hero-installment.jpg";
import smallBusinessUrl from "@/assets/heroes/hero-small-business.jpg";
import unfiledUrl from "@/assets/heroes/hero-unfiled.jpg";
import hardshipUrl from "@/assets/heroes/hero-hardship.jpg";
import lienUrl from "@/assets/heroes/hero-lien.jpg";
import penaltyClockUrl from "@/assets/heroes/hero-penalty-clock.jpg";
import federalColumnsUrl from "@/assets/heroes/hero-federal-columns.jpg";
import reliefSunriseUrl from "@/assets/heroes/hero-relief-sunrise.jpg";

export interface StockImage {
  id: string;
  src: string;
  alt: string;
  credit: { name: string; href: string };
}

const aiCredit = { name: "AI generated", href: "" };

const img = (
  asset: { asset_id: string; url: string },
  alt: string,
): StockImage => ({ id: asset.asset_id, src: asset.url, alt, credit: aiCredit });

const bundled = (id: string, url: string, alt: string): StockImage => ({ id, src: url, alt, credit: aiCredit });

export const stockImages = {
  taxFormsCalculator: img(taxFormsAsset, "CPA desk with US tax forms, calculator and laptop"),
  taxWithholdingForm: img(taxFormsAsset, "Close-up of US tax forms with calculator and pen"),
  taxFormsStack: img(taxFormsAsset, "Stack of US tax forms on a desk"),
  signingForm: img(signingAsset, "Hand signing an IRS document with a fountain pen"),
  accountingDesk: img(accountingAsset, "Accountant's desk with ledger, calculator and reading glasses under a lamp"),
  countingCash: img(moneyAsset, "Stacks of US one hundred dollar bills on a dark wood desk"),
  consultation: img(consultationAsset, "Two professionals reviewing paperwork at a conference table"),
  officeProfessional: img(consultationAsset, "Professionals collaborating in a modern office"),
  financialChart: img(chartAsset, "Upward-trending financial chart on paper with magnifier and pen"),
  analyticsDashboard: img(chartAsset, "Financial analytics chart on paper"),
  piggyBank: img(savingsAsset, "Pink piggy bank with stacks of coins on a wood desk"),
  irsBuilding: img(irsBuildingAsset, "Federal government-style building exterior at golden hour"),
  noticeEnvelope: img(noticeAsset, "Opened envelope with a folded letter on a dark wood desk"),

  wageGarnishment: bundled("wage-garnishment", wageGarnishmentUrl, "Pay stub showing reduced take-home pay beside a calculator"),
  bankLevy: bundled("bank-levy", bankLevyUrl, "Frozen padlock resting on a bank statement printout"),
  audit: bundled("audit", auditUrl, "Magnifying glass over tax documents and spreadsheets"),
  settlement: bundled("settlement", settlementUrl, "Resolved settlement folders on a conference table"),
  installment: bundled("installment", installmentUrl, "Desk calendar with circled monthly payment dates and dollar bills"),
  smallBusiness: bundled("small-business", smallBusinessUrl, "Small American main-street storefront glowing at dusk"),
  unfiled: bundled("unfiled", unfiledUrl, "Tall stacks of manila file folders under a desk lamp"),
  hardship: bundled("hardship", hardshipUrl, "Empty open wallet on a worn kitchen table"),
  lien: bundled("lien", lienUrl, "Official red-stamped legal document with a fountain pen"),
  penaltyClock: bundled("penalty-clock", penaltyClockUrl, "Vintage brass desk clock beside stacks of US coins"),
  federalColumns: bundled("federal-columns", federalColumnsUrl, "Columns of a federal-style government building at golden hour"),
  reliefSunrise: bundled("relief-sunrise", reliefSunriseUrl, "Aerial sunrise over a quiet American suburb"),
} as const;

export type StockImageKey = keyof typeof stockImages;

const HERO_RULES: { keywords: string[]; image: StockImage }[] = [
  { keywords: ["wage garnishment", "wage-garnishment", "garnishment", "wage levy", "paycheck"], image: stockImages.wageGarnishment },
  { keywords: ["bank levy", "bank-levy", "frozen account"], image: stockImages.bankLevy },
  { keywords: ["audit", "examination"], image: stockImages.audit },
  { keywords: ["offer in compromise", "oic", "settle", "settlement", "pennies"], image: stockImages.settlement },
  { keywords: ["installment", "payment plan", "monthly payment"], image: stockImages.installment },
  { keywords: ["payroll", "941", "trust fund", "business tax", "small business"], image: stockImages.smallBusiness },
  { keywords: ["unfiled", "back taxes", "back-taxes", "missing return"], image: stockImages.unfiled },
  { keywords: ["cnc", "currently not collectible", "hardship", "innocent spouse"], image: stockImages.hardship },
  { keywords: ["lien", "federal tax lien"], image: stockImages.lien },
  { keywords: ["penalty", "abatement", "interest"], image: stockImages.penaltyClock },
  { keywords: ["irs notice", "cp14", "cp2000", "lt11", "notice", "letter"], image: stockImages.noticeEnvelope },
  { keywords: ["collections", "stop collections", "levy"], image: stockImages.bankLevy },
  { keywords: ["representation", "power of attorney", "circular 230", "methodology"], image: stockImages.federalColumns },
  { keywords: ["resolution", "relief", "fresh start", "results"], image: stockImages.reliefSunrise },
];

const DEFAULT_HEROES = [
  stockImages.accountingDesk,
  stockImages.taxFormsCalculator,
  stockImages.consultation,
  stockImages.financialChart,
];

interface MatchablePost {
  slug?: string;
  title?: string;
  category?: string;
}

export function pickPostHeroImage(post: MatchablePost): StockImage {
  const haystack = `${post.slug ?? ""} ${post.title ?? ""} ${post.category ?? ""}`.toLowerCase();
  for (const rule of HERO_RULES) {
    if (rule.keywords.some((k) => haystack.includes(k))) return rule.image;
  }
  const seed = (post.slug ?? post.title ?? "").length;
  return DEFAULT_HEROES[seed % DEFAULT_HEROES.length];
}
